import { Injectable, signal, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export type NetworkStatus = 'online' | 'offline' | 'slow';

@Injectable({
  providedIn: 'root',
})
export class NetworkStatusService {
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  private status = signal<NetworkStatus>('online');
  private lastToastId?: number;

  constructor() {
    // Monitor online/offline status
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Monitor connection quality
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', () => this.handleConnectionChange(connection));
    }
  }

  private handleOnline(): void {
    // Only show toast if status was previously offline
    if (this.status() !== 'online') {
      this.status.set('online');
      this.showStatusToast('online');
    }
  }

  private handleOffline(): void {
    this.status.set('offline');
    this.showStatusToast('offline');
  }

  private handleConnectionChange(connection: any): void {
    const currentStatus = this.status();

    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g' || connection.downlink < 0.5) {
      // Only update if status changed
      if (currentStatus !== 'slow') {
        this.status.set('slow');
        this.showStatusToast('slow');
      }
    } else {
      // Only update if status changed and wasn't already online
      if (currentStatus !== 'online') {
        this.status.set('online');
        this.showStatusToast('online');
      }
    }
  }

  private showStatusToast(status: NetworkStatus): void {
    // Remove previous toast if exists
    if (this.lastToastId) {
      this.toastr.remove(this.lastToastId);
    }

    const message = this.translate.instant(`NETWORK_STATUS.${status.toUpperCase()}`);
    const toast = this.toastr.info(message, '', {
      timeOut: 0,
      extendedTimeOut: 0,
      closeButton: true,
      positionClass: 'toast-top-center',
    });

    this.lastToastId = toast.toastId;
  }

  public getStatus(): NetworkStatus {
    return this.status();
  }

  public isOnline(): boolean {
    return this.status() === 'online';
  }

  public isOffline(): boolean {
    return this.status() === 'offline';
  }

  public isSlowConnection(): boolean {
    return this.status() === 'slow';
  }
}
